/**
 * THE PAYOUT BRAKE (U-03) as ONE frontend statement — the single place in this module where the
 * frontend still decides for itself whether an agent is braked.
 *
 * <h2>What the rule is, and where it really lives</h2>
 * The authority is the backend: {@code ProvisionPayoutService.reserveUnderfunded(BigDecimal)} —
 * "a negative REAL reserve balance blocks the payout of this agent" — is the predicate the payout
 * run itself calls in its guard, and it is deliberately stated there exactly once so that the
 * warning and the run cannot drift apart. The statement endpoint already publishes its verdict as
 * {@code ProvisionAccountStatementResponseDto.reserveUnderfunded}.
 *
 * <p><b>Reading that flag is always better than calling this function.</b>
 * {@code CpProvisionAgentAccountComponent} does exactly that and must keep doing it: it takes the
 * brake from the response and derives nothing. This file exists ONLY for the surface that has no
 * such source.
 *
 * <h2>Why the surface without a source exists (and what would remove this file)</h2>
 * {@code CpProvisionAgentBalanceComponent} — the side view that survives per FD-3 — reads the
 * persisted row {@code cp.ProvisionAgentBalance} through the GENERIC CRUD service and never calls
 * the statement endpoint. That row carries the four balance figures and no brake flag, so the
 * component has nothing to read. Calling the statement endpoint from a compact side panel just to
 * learn one boolean would fetch a whole page of bookings, an opening-balance aggregate and two
 * counts — six statements by the service's own pinned budget — and throw all of it away: a cost
 * out of all proportion to the question.
 *
 * <p>The proper fix is a BACKEND one and is not made here: publish the same
 * {@code ProvisionPayoutService.reserveUnderfunded(...)} verdict as a derived field on the
 * {@code cp.ProvisionAgentBalance} read path. The moment that field exists, this file is DELETED
 * and its one caller reads the flag like the account component does. Until then the rule is stated
 * in the frontend exactly ONCE — here — instead of being open-coded inside a component template's
 * source, which is what it was.
 *
 * <h2>Rules for whoever touches this</h2>
 * <ul>
 *   <li>Do NOT add a second caller that HAS the delivered flag. A reader with a source must read it.
 *       This function is for the source-less surface alone.</li>
 *   <li>Do NOT add a simulated variant here. A negative SIMULATED reserve balance is a FORECAST, not
 *       this brake: the run brakes on the REAL balance alone, and the backend ships no flag for the
 *       simulated case on purpose. Naming it an "Unterdeckung" in the same words would leave an
 *       operator unable to tell which of two red statements actually stops a payout.</li>
 *   <li>Do NOT let the two drift. If the backend predicate ever gains a tolerance or a coverage
 *       ratio, this mirror is wrong the same day — which is precisely why the backend field above is
 *       the real answer and this is the interim one.</li>
 * </ul>
 */

/**
 * Whether the payout brake applies — the frontend MIRROR of
 * {@code ProvisionPayoutService.reserveUnderfunded(BigDecimal)}.
 *
 * @param reserveBalance the REAL reserve balance of the agent, i.e.
 *                       {@code ProvisionAgentBalanceDto.reserveBalance}. `null`/`undefined` counts
 *                       as NOT underfunded — an agent whose balance job has never run has nothing to
 *                       be short of, and no alarm may be raised over a figure nobody computed. The
 *                       backend states the same for its `null`.
 * @return true when the reserve account blocks the payout of this agent
 */
export function cpReserveUnderfunded(reserveBalance: number): boolean {
    return reserveBalance != null && reserveBalance < 0;
}
